// src/oura/components/onboarding/useBaselineSnapshot.ts
//
// One scan's Baseline Snapshot, read and followed while Oura makes it. Shared
// by the staff row on Onboarding (#4423) and the merchant's Reports drawer
// (#4457), which only reads.

import { useCallback, useEffect, useRef, useState } from "react";
import { getBaselineSnapshot, makeBaselineSnapshot, type SnapshotRead } from "@service/aeo/baselineSnapshot";

/** While Oura is making it, read again this often (the ticket's 5 s). */
const POLL_MS = 5_000;

type Load = { state: "loading" } | { state: "read"; read: SnapshotRead } | { state: "failed"; message: string };

/** The snapshot for one scan: read on mount, read again every 5 s while it is being made. */
export function useBaselineSnapshot(assessmentId: string) {
    const [load, setLoad] = useState<Load>({ state: "loading" });
    const [starting, setStarting] = useState(false);
    const [refused, setRefused] = useState<string | null>(null);
    // Only the newest read may paint: a slow read must not overwrite a newer one.
    const seq = useRef(0);

    const read = useCallback(async () => {
        const mine = ++seq.current;
        const r = await getBaselineSnapshot(assessmentId);
        if (mine !== seq.current) return;
        setLoad(r.ok ? { state: "read", read: r.read } : { state: "failed", message: r.message });
    }, [assessmentId]);

    useEffect(() => {
        setLoad({ state: "loading" });
        setStarting(false);
        setRefused(null);
        void read();
        return () => {
            seq.current += 1;
        };
    }, [read]);

    const making = load.state === "read" && load.read.status === "making";
    useEffect(() => {
        if (!making) return;
        const timer = window.setTimeout(() => void read(), POLL_MS);
        return () => window.clearTimeout(timer);
    }, [making, load, read]);

    const make = useCallback(async () => {
        // The make belongs to this scan: an answer that lands after a switch to another one is dropped.
        const mine = ++seq.current;
        setStarting(true);
        setRefused(null);
        const r = await makeBaselineSnapshot(assessmentId);
        if (mine !== seq.current) return;
        setStarting(false);
        if (!r.ok) {
            setRefused(r.message);
            return;
        }
        setLoad({ state: "read", read: r.read });
    }, [assessmentId]);

    return { load, starting, refused, read, make };
}
